/**
 * `user-actions`-komponentin datamalli ja esitysapurit (WP-UA4,
 * user-action-plan.md §3.4).
 *
 * <p>Tässä on <b>vain puhdas logiikka</b> — jäsennys, kohdennus vastaukseen ja
 * määräajan esitys. DOM-kytkentä (`wireUserActions`) elää `runtime.ts`:ssä
 * muiden kytkentöjen rinnalla; sama työnjako kuin `dataGrid`/`kanban`/`gantt`
 * -moduuleilla.
 *
 * <p>Toimet EIVÄT tule Appin datalähteestä vaan sillan omista operaatioista:
 * ne ovat <i>alustan</i> dataa (kenelle toimi on osoitettu, kuka katsoo), ei
 * Appin deklaroimaa reittiä. Sama ratkaisu kuin `currentUser`-siemenellä ja
 * dokumenttioperaatioilla — App ei koskaan nimeä reittiä jolla ne haetaan.
 */

/** Yksi vaihtoehto kuten palvelin sen antaa. */
export interface UserActionOption {
  key: string;
  label: string;
}

/** Yksi käyttäjätoimi runtimen käyttämässä normalisoidussa muodossa. */
export interface UserActionItem {
  id: string;
  title: string;
  message: string;
  options: UserActionOption[];
  optionType: 'single' | 'multiple';
  allowComment: boolean;
  /** Näytettävä konteksti avain–arvo-pareina (tekstikonteksti → yksi rivi). */
  context: { label: string; value: string }[];
  queue: string;
  status: string;
  expiresAt: string;
  /** Ratkaistun toimen tiivistelmä ("approve", "unit, e2e" tai kommentti). */
  answer: string;
  answeredBy: string;
  /**
   * Odottavan prosessin jatko (`user-action:await`): reitti pysähtyi tähän toimeen.
   * Vain reitti ja jatkon tila — tilannekuva ei koskaan tule katsojalle.
   */
  continuation?: { routeId: string; status: string; detail: string };
}

/** Vastaus jonka runtime lähettää sillan yli. */
export interface UserActionAnswer {
  selectedOption?: string;
  selectedOptions?: string[];
  comment?: string;
}

const MAX_CONTEXT_ROWS = 12;

/** Onko toimi vielä ratkaistavissa? */
export function isPending(action: UserActionItem): boolean {
  return action.status === 'PENDING';
}

/**
 * Jäsennä palvelimen lista normalisoiduiksi toimiksi. Kelvoton alkio
 * OHITETAAN: yksi rikkinäinen tietue ei saa tyhjentää koko inboxia, ja
 * puuttuvat kentät ovat aina mahdollisia kun data tulee eri versiolta.
 */
export function parseUserActions(raw: unknown): UserActionItem[] {
  if (!Array.isArray(raw)) return [];
  const out: UserActionItem[] = [];
  for (const item of raw) {
    const parsed = parseOne(item);
    if (parsed) out.push(parsed);
  }
  return out;
}

function parseOne(raw: unknown): UserActionItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  const id = typeof rec.id === 'string' ? rec.id : '';
  if (!id) return null;
  const message = typeof rec.message === 'string' ? rec.message : '';
  const title = typeof rec.title === 'string' && rec.title ? rec.title : message;
  const response = (rec.response && typeof rec.response === 'object'
    ? rec.response as Record<string, unknown>
    : null);
  return {
    id,
    title,
    message,
    options: parseOptions(rec.options),
    optionType: rec.optionType === 'multiple' ? 'multiple' : 'single',
    allowComment: rec.allowComment !== false,
    context: parseContext(rec.contextData),
    queue: typeof rec.queue === 'string' ? rec.queue : '',
    status: typeof rec.status === 'string' ? rec.status : 'PENDING',
    expiresAt: typeof rec.expiresAt === 'string' ? rec.expiresAt : '',
    answer: summarizeAnswer(response),
    answeredBy: response && typeof response.respondedBy === 'string' ? response.respondedBy : '',
    continuation: parseContinuation(rec.continuation),
  };
}

function parseContinuation(raw: unknown): UserActionItem['continuation'] {
  if (!raw || typeof raw !== 'object') return undefined;
  const rec = raw as Record<string, unknown>;
  const routeId = typeof rec.routeId === 'string' ? rec.routeId : '';
  if (!routeId) return undefined;
  return {
    routeId,
    status: typeof rec.status === 'string' ? rec.status : 'PENDING',
    detail: typeof rec.detail === 'string' ? rec.detail : '',
  };
}

function parseOptions(raw: unknown): UserActionOption[] {
  if (!Array.isArray(raw)) return [];
  const out: UserActionOption[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    const key = typeof rec.key === 'string' ? rec.key : '';
    if (!key) continue;
    out.push({ key, label: typeof rec.label === 'string' && rec.label ? rec.label : key });
  }
  return out;
}

/**
 * Konteksti näytettävään muotoon. Objekti puretaan riveiksi, muu arvo on yksi
 * rivi ilman labelia — sisäkkäinen rakenne näytetään JSONina, koska
 * mielivaltaisen syvän puun piirtäminen ei kuulu tälle komponentille.
 */
export function parseContext(raw: unknown): { label: string; value: string }[] {
  if (raw === null || raw === undefined || raw === '') return [];
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const rows: { label: string; value: string }[] = [];
    for (const [label, value] of Object.entries(raw as Record<string, unknown>)) {
      if (rows.length >= MAX_CONTEXT_ROWS) break;
      rows.push({ label, value: scalarText(value) });
    }
    return rows;
  }
  return [{ label: '', value: scalarText(raw) }];
}

function scalarText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function summarizeAnswer(response: Record<string, unknown> | null): string {
  if (!response) return '';
  if (typeof response.selectedOption === 'string' && response.selectedOption) {
    return response.selectedOption;
  }
  if (Array.isArray(response.selectedOptions) && response.selectedOptions.length > 0) {
    return response.selectedOptions.map((v) => String(v)).join(', ');
  }
  return typeof response.comment === 'string' ? response.comment : '';
}

/**
 * Määräaika näyttömuotoon: paikallinen `YYYY-MM-DD HH:mm`.
 *
 * <p>Tarkoituksellisesti absoluuttinen aika eikä "2 pv jäljellä": suhteellinen
 * muoto vaatisi aikayksiköiden kääntämisen jokaiselle kielelle, ja määräajassa
 * kysytään yleensä *milloin*, ei *kuinka kauan*. Tyhjä syöte → tyhjä tulos.
 */
export function formatDeadline(iso: string): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  const d = new Date(ms);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} `
    + `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Onko määräaika mennyt? (Vain esitystä varten — palvelin ratkaisee tilan.) */
export function isOverdue(iso: string, nowMs: number = Date.now()): boolean {
  if (!iso) return false;
  const ms = Date.parse(iso);
  return !Number.isNaN(ms) && ms < nowMs;
}

/**
 * Kokoa vastaus lomakkeen tilasta ja kerro miksi se ei kelpaa.
 *
 * <p>Validointi tehdään TÄSSÄ eikä vain palvelimella, jotta käyttäjä saa
 * välittömän palautteen — mutta palvelin validoi saman uudelleen, koska
 * selaimen tarkistus ei ole portti.
 */
export function buildAnswer(
  action: UserActionItem,
  selected: string[],
  comment: string,
): { answer: UserActionAnswer } | { error: 'choice' | 'comment' } {
  const trimmed = comment.trim();
  if (action.options.length === 0) {
    if (!trimmed) return { error: 'comment' };
    return { answer: { comment: trimmed } };
  }
  const known = selected.filter((key) => action.options.some((o) => o.key === key));
  if (known.length === 0) return { error: 'choice' };
  if (action.optionType === 'multiple') {
    return { answer: { selectedOptions: known, ...(trimmed ? { comment: trimmed } : {}) } };
  }
  return { answer: { selectedOption: known[0], ...(trimmed ? { comment: trimmed } : {}) } };
}
